import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  CreditCard,
  Search,
  Filter,
  Crown,
  Sparkles,
  Clock,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Users,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Plus,
  Minus,
  MoreVertical,
  Eye,
  RefreshCw,
  X,
  FlaskConical,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDaysRemaining = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diff = expiry - now;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days;
};

const SubscriptionBadge = ({ type, active, expiryDate }) => {
  const daysRemaining = getDaysRemaining(expiryDate);
  const isExpiringSoon = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 7;

  if (!active && daysRemaining !== null && daysRemaining <= 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-gray-600">
        <XCircle className="w-3 h-3" />
        Expired
      </span>
    );
  }

  if (active) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
          isExpiringSoon
            ? 'bg-amber-100 text-amber-700'
            : type === 'premium'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        )}
      >
        {type === 'premium' ? <Crown className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
        {type === 'premium' ? 'Premium' : 'AI Plan'}
        {isExpiringSoon && <AlertTriangle className="w-3 h-3 ml-1" />}
      </span>
    );
  }

  return null;
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-card rounded-lg border border-border p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <div className={cn('p-3 rounded-lg', color)}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const SubscriptionsPage = () => {
  const { user } = useCompanyAdminAuth();
  const isAgent = Number(user?.user_type) === 3;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Extend form state
  const [extendType, setExtendType] = useState('premium');
  const [extendDays, setExtendDays] = useState(30);
  const [extendReason, setExtendReason] = useState('');

  // Cancel form state
  const [cancelType, setCancelType] = useState('premium');
  const [cancelReason, setCancelReason] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const res = await companyAdminCustomersAPI.getSubscriptionStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim(),
        status: statusFilter !== 'all' ? statusFilter : undefined,
        subscription_type: typeFilter !== 'all' ? typeFilter : undefined,
      };
      const res = await companyAdminCustomersAPI.getSubscriptions(params);
      setSubscriptions(res.data.subscriptions || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        pages: res.data.pages,
      }));
    } catch (error) {
      toast.error('Failed to load subscriptions');
      console.error('Failed to load subscriptions:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter, typeFilter]);

  useEffect(() => {
    loadStats();
    loadSubscriptions();
  }, [loadStats, loadSubscriptions]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadSubscriptions();
  };

  const handleViewDetail = async (customer) => {
    setSelectedCustomer(customer);
    setDetailModalOpen(true);
    try {
      const res = await companyAdminCustomersAPI.getSubscriptionDetail(customer.user_id);
      setCustomerDetail(res.data);
    } catch (error) {
      toast.error('Failed to load subscription details');
    }
  };

  const handleOpenExtend = (customer) => {
    setSelectedCustomer(customer);
    setExtendType('premium');
    setExtendDays(30);
    setExtendReason('');
    setExtendModalOpen(true);
  };

  const handleOpenCancel = (customer) => {
    setSelectedCustomer(customer);
    setCancelType('premium');
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const handleExtendSubscription = async () => {
    if (!selectedCustomer) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.extendSubscription(selectedCustomer.user_id, {
        subscription_type: extendType,
        days: extendDays,
        reason: extendReason,
      });
      toast.success(`Subscription extended by ${extendDays} days`);
      setExtendModalOpen(false);
      loadSubscriptions();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedCustomer) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.cancelSubscription(selectedCustomer.user_id, {
        subscription_type: cancelType,
        reason: cancelReason,
      });
      toast.success('Subscription cancelled successfully');
      setCancelModalOpen(false);
      loadSubscriptions();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Subscriptions</h1>
            <p className="text-muted-foreground mt-1">Manage customer subscriptions and billing</p>
          </div>
          <Button onClick={() => { loadStats(); loadSubscriptions(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <StatCard
              title="Total Customers"
              value={stats.total_customers}
              icon={Users}
              color="bg-slate-500"
            />
            <StatCard
              title="Active Premium"
              value={stats.active_premium}
              icon={Crown}
              color="bg-purple-500"
            />
            <StatCard
              title="Active AI Plan"
              value={stats.active_ai_plan}
              icon={Sparkles}
              color="bg-blue-500"
            />
            <StatCard
              title="Expired Premium"
              value={stats.expired_premium}
              icon={XCircle}
              color="bg-gray-500"
            />
            <StatCard
              title="Expired AI Plan"
              value={stats.expired_ai_plan}
              icon={XCircle}
              color="bg-gray-500"
            />
            <StatCard
              title="Free Users"
              value={stats.free_users}
              icon={Users}
              color="bg-green-500"
            />
            <StatCard
              title="Expiring Soon"
              value={stats.expiring_soon}
              icon={AlertTriangle}
              color="bg-amber-500"
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm mb-1.5 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <Label className="text-sm mb-1.5 block">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <Label className="text-sm mb-1.5 block">Type</Label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="ai_plan">AI Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit">
              <Filter className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
          </form>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Customer</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Premium Expiry</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">AI Plan Expiry</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Joined</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.user_id} className="border-b border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-foreground">{sub.name || 'Unnamed'}</p>
                          <p className="text-sm text-muted-foreground">{sub.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {sub.trial === 1 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              <FlaskConical className="w-3 h-3" />
                              Trial
                            </span>
                          ) : (
                            <>
                              <SubscriptionBadge
                                type="premium"
                                active={sub.subscription_status?.premium?.active}
                                expiryDate={sub.subscription_status?.premium?.expiry_date}
                              />
                              <SubscriptionBadge
                                type="ai_plan"
                                active={sub.subscription_status?.ai_plan?.active}
                                expiryDate={sub.subscription_status?.ai_plan?.expiry_date}
                              />
                              {!sub.subscription_status?.premium?.active && !sub.subscription_status?.ai_plan?.active && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-gray-600">
                                  Free
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {sub.subscription_status?.premium?.expiry_date ? (
                          <div>
                            <p>{formatDate(sub.subscription_status.premium.expiry_date)}</p>
                            {getDaysRemaining(sub.subscription_status.premium.expiry_date) > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {getDaysRemaining(sub.subscription_status.premium.expiry_date)} days left
                              </p>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {sub.subscription_status?.ai_plan?.expiry_date ? (
                          <div>
                            <p>{formatDate(sub.subscription_status.ai_plan.expiry_date)}</p>
                            {getDaysRemaining(sub.subscription_status.ai_plan.expiry_date) > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {getDaysRemaining(sub.subscription_status.ai_plan.expiry_date)} days left
                              </p>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(sub.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetail(sub)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {!isAgent && (
                              <>
                                <DropdownMenuItem onClick={() => handleOpenExtend(sub)}>
                                  <Plus className="w-4 h-4 mr-2" />
                                  Extend Subscription
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleOpenCancel(sub)}
                                  className="text-destructive"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel Subscription
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subscription Details</DialogTitle>
            <DialogDescription>
              {selectedCustomer?.name || selectedCustomer?.email}
            </DialogDescription>
          </DialogHeader>

          {customerDetail ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="transactions">Transactions</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold">Premium Subscription</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className={customerDetail.subscription_status?.premium?.active ? 'text-green-600' : 'text-muted-foreground'}>
                          {customerDetail.subscription_status?.premium?.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Signup Date:</span>
                        <span>{formatDate(customerDetail.subscription_status?.premium?.signup_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expiry Date:</span>
                        <span>{formatDate(customerDetail.subscription_status?.premium?.expiry_date)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold">AI Plan</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className={customerDetail.subscription_status?.ai_plan?.active ? 'text-green-600' : 'text-muted-foreground'}>
                          {customerDetail.subscription_status?.ai_plan?.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expiry Date:</span>
                        <span>{formatDate(customerDetail.subscription_status?.ai_plan?.expiry_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Customer Info</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span>{customerDetail.customer?.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span>{customerDetail.customer?.email || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span>{customerDetail.customer?.status || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Joined:</span>
                      <span>{formatDate(customerDetail.customer?.created_at)}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="transactions" className="mt-4">
                <div className="space-y-2">
                  {customerDetail.transactions?.length > 0 ? (
                    customerDetail.transactions.map((tx, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{tx.product_id}</p>
                            <p className="text-muted-foreground text-xs">{tx.platform} • {tx.subscription_type}</p>
                          </div>
                          <span className="text-xs text-muted-foreground">{formatDateTime(tx.createdAt)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">TX: {tx.transaction_id}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No transactions found</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {customerDetail.history?.length > 0 ? (
                    customerDetail.history.map((h, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium capitalize">{h.event_type?.replace(/_/g, ' ')}</p>
                            <p className="text-muted-foreground text-xs">
                              {h.subscription_type} • {h.platform} • {h.source}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground">{formatDateTime(h.createdAt)}</span>
                        </div>
                        {h.raw_notification_type && (
                          <p className="text-xs text-muted-foreground mt-1">{h.raw_notification_type}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No history found</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Modal */}
      <Dialog open={extendModalOpen} onOpenChange={setExtendModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Subscription</DialogTitle>
            <DialogDescription>
              Extend subscription for {selectedCustomer?.name || selectedCustomer?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Subscription Type</Label>
              <Select value={extendType} onValueChange={setExtendType}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="ai_plan">AI Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Days to Extend</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtendDays(Math.max(1, extendDays - 7))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtendDays(extendDays + 7)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 mt-2">
                {[7, 30, 90, 365].map((d) => (
                  <Button
                    key={d}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setExtendDays(d)}
                    className={cn(extendDays === d && 'bg-primary text-white')}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
                placeholder="e.g., Promotional offer"
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExtendSubscription} disabled={actionLoading}>
              {actionLoading ? 'Extending...' : `Extend by ${extendDays} days`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Cancel subscription for {selectedCustomer?.name || selectedCustomer?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Subscription Type to Cancel</Label>
              <Select value={cancelType} onValueChange={setCancelType}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium Only</SelectItem>
                  <SelectItem value="ai_plan">AI Plan Only</SelectItem>
                  <SelectItem value="all">All Subscriptions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Customer request"
                className="mt-1.5"
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              This will immediately deactivate the selected subscription(s). The customer will lose access to premium features.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancelSubscription} disabled={actionLoading}>
              {actionLoading ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CompanyAdminLayout>
  );
};

export default SubscriptionsPage;
